/**
 * Core Application Component
 *
 * This component serves as the main wrapper for the entire application.
 * It handles:
 * - Authentication flow via AuthProvider
 * - Session verification via AuthVerify
 * - Global logout functionality
 *
 * Authentication Flow:
 * 1. User lands on app -> AuthProvider checks for token
 * 2. No token -> Redirect to Login page
 * 3. Token exists -> AuthVerify validates session
 * 4. Valid session -> Render AuthorizedApp with full routing
 * 5. Invalid/expired -> logOut() clears state and redirects to login
 *
 * @see src/auth/AuthProvider.jsx - Handles login/token management
 * @see src/auth/AuthVerify/AuthVerify.jsx - Session validation
 * @see src/auth/AuthorizedApp.jsx - Authenticated app shell
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Login } from './datastage/pages/Login';
import { AuthorizedApp } from 'auth/AuthorizedApp';
import { AuthProvider } from 'auth/AuthProvider';
import { AuthVerify } from 'auth/AuthVerify/AuthVerify';
import { resetActiveUser } from './datastage/utils/localStorageService';
import { logout } from './datastage/store/auth';

export function Core() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /**
     * Handles user logout across the application
     * - Clears user data from localStorage
     * - Resets Redux auth state
     * - Redirects to login page
     */
    function logOut() {
        resetActiveUser();
        dispatch(logout());
        navigate('/login');
    }

    return (
        <div id="app" className="d-flex flex-column min-vh-100">
            <AuthProvider LoginComp={Login} AppComp={AuthorizedApp} />
            <AuthVerify logOut={logOut} />
        </div>
    );
}
